if (!customElements.get('gma-free-shipping')) {
  customElements.define(
    'gma-free-shipping',
    class GmaFreeShipping extends HTMLElement {
      static MESSAGE_VARIANT_KEY = 'gma-free-shipping-msg-variant';

      connectedCallback() {
        if (this.hasAttribute('data-gma-free-shipping-ready')) return;

        this.innerEl = this.querySelector('[data-gma-free-shipping-inner]');
        this.messageEl = this.querySelector('[data-gma-free-shipping-message]');
        this.progressEl = this.querySelector('[data-gma-free-shipping-progress]');
        this.barEl = this.querySelector('[data-gma-free-shipping-bar]');
        this.goalEl = this.querySelector('[data-gma-free-shipping-goal]');
        this.threshold = Number(this.dataset.threshold || 0);
        this.currency = this.dataset.currency || 'AUD';
        this.locale = this.dataset.locale || 'en-AU';
        this.msgQualified = this.dataset.msgQualified || "You've unlocked free shipping on this order";
        this.messageVariant = null;
        this.lastTotalPrice = null;
        this.messageSwapTimer = null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.ensureMessageVariant();

        // Trust Liquid SSR on first paint — avoid fetch flash.
        this.lastTotalPrice = this.hasAttribute('data-initial-total')
          ? Number(this.dataset.initialTotal)
          : this.classList.contains('is-open')
            ? -1
            : 0;

        this.onCartUpdate = this.onCartUpdate.bind(this);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        }

        this.setAttribute('data-gma-free-shipping-ready', '');
        if (this.classList.contains('is-open')) {
          this.removeAttribute('hidden');
          this.setAttribute('aria-hidden', 'false');
        } else {
          this.setAttribute('hidden', '');
          this.setAttribute('aria-hidden', 'true');
        }
      }

      disconnectedCallback() {
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
        if (this.messageSwapTimer) window.clearTimeout(this.messageSwapTimer);
      }

      ensureMessageVariant() {
        if (this.messageVariant != null) return this.messageVariant;

        try {
          const stored = window.sessionStorage?.getItem(GmaFreeShipping.MESSAGE_VARIANT_KEY);
          if (stored !== null && stored !== '') {
            this.messageVariant = Number(stored) % 3;
            this.dataset.messageVariant = String(this.messageVariant);
            return this.messageVariant;
          }
        } catch (error) {
          // sessionStorage may be unavailable.
        }

        if (this.dataset.messageVariant !== undefined && this.dataset.messageVariant !== '') {
          this.messageVariant = Number(this.dataset.messageVariant) % 3;
        }

        if (this.messageVariant == null || Number.isNaN(this.messageVariant)) {
          this.messageVariant = Math.floor(Math.random() * 3);
        }

        this.dataset.messageVariant = String(this.messageVariant);

        try {
          window.sessionStorage?.setItem(
            GmaFreeShipping.MESSAGE_VARIANT_KEY,
            String(this.messageVariant)
          );
        } catch (error) {
          // Ignore persistence failures.
        }

        return this.messageVariant;
      }

      onCartUpdate() {
        this.refresh();
      }

      refresh() {
        if (!this.threshold || this.threshold <= 0) {
          this.close();
          return Promise.resolve();
        }

        return this.fetchCartTotal()
          .then((totalPrice) => {
            if (this.lastTotalPrice === totalPrice) return;
            this.lastTotalPrice = totalPrice;
            this.render(totalPrice);
          })
          .catch(() => {
            // Keep current state if cart.js is unavailable.
          });
      }

      async fetchCartTotal() {
        const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        const response = await fetch(cartUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });

        if (!response.ok) throw new Error('Unable to load cart');

        const cart = await response.json();
        return Number(cart.total_price || 0);
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;
        try {
          const formatted = new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(amount);

          return formatted.replace(/([.,]00)(?!\d)/, '');
        } catch (error) {
          return `$${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
        }
      }

      buildProgressMessage(remaining) {
        const remainingMoney = this.formatMoney(remaining);
        const thresholdMoney = this.formatMoney(this.threshold);
        const variant = this.ensureMessageVariant();

        switch (variant) {
          case 1:
            return `${remainingMoney} more unlocks free shipping`;
          case 2:
            return `Free shipping at ${thresholdMoney} – you're ${remainingMoney} short`;
          case 0:
          default:
            return `Add ${remainingMoney} more for free shipping`;
        }
      }

      render(totalPrice) {
        if (!totalPrice || totalPrice <= 0) {
          this.close();
          return;
        }

        const remaining = Math.max(this.threshold - totalPrice, 0);
        const qualified = remaining === 0;
        let percentage = Math.round((totalPrice * 100) / this.threshold);
        if (percentage > 100) percentage = 100;
        if (percentage < 0) percentage = 0;

        const nextMessage = qualified ? this.msgQualified : this.buildProgressMessage(remaining);

        this.classList.toggle('gma-free-shipping--qualified', qualified);
        this.setMessage(nextMessage);
        this.open();
        this.setProgress(percentage, !qualified);
      }

      setMessage(nextMessage) {
        if (!this.messageEl) return;

        const current = this.messageEl.textContent.trim();
        if (current === nextMessage) return;

        if (!this.classList.contains('is-open') || this.reduceMotion || !current) {
          this.messageEl.textContent = nextMessage;
          this.messageEl.classList.remove('is-fading');
          return;
        }

        this.messageEl.classList.add('is-fading');
        if (this.messageSwapTimer) window.clearTimeout(this.messageSwapTimer);
        this.messageSwapTimer = window.setTimeout(() => {
          this.messageEl.textContent = nextMessage;
          this.messageEl.classList.remove('is-fading');
        }, 180);
      }

      setProgress(percentage, visible) {
        if (!this.progressEl) return;

        this.progressEl.classList.toggle('is-hidden', !visible);
        this.progressEl.setAttribute('aria-valuenow', String(visible ? percentage : 0));
        this.progressEl.setAttribute('aria-hidden', visible ? 'false' : 'true');

        if (!this.barEl) return;

        const nextWidth = `${visible ? percentage : 0}%`;

        if (this.goalEl) {
          this.goalEl.style.setProperty('--gma-free-shipping-progress', nextWidth);
        }

        if (!visible || this.reduceMotion) {
          this.barEl.style.width = nextWidth;
          return;
        }

        // Restart fill so the bar never appears "stuck" empty after open.
        this.barEl.style.transition = 'none';
        this.barEl.style.width = '0%';
        if (this.goalEl) {
          this.goalEl.style.transition = 'none';
          this.goalEl.style.setProperty('--gma-free-shipping-progress', '0%');
        }
        void this.barEl.offsetWidth;
        this.barEl.style.transition = '';
        if (this.goalEl) this.goalEl.style.transition = '';
        requestAnimationFrame(() => {
          this.barEl.style.width = nextWidth;
          if (this.goalEl) {
            this.goalEl.style.setProperty('--gma-free-shipping-progress', nextWidth);
          }
        });
      }

      open() {
        this.removeAttribute('hidden');
        this.setAttribute('aria-hidden', 'false');
        // Force a frame so the closed grid state can transition.
        if (!this.classList.contains('is-open')) {
          this.style.gridTemplateRows = '0fr';
          this.classList.add('is-open');
          void this.offsetHeight;
          this.style.gridTemplateRows = '';
        }
      }

      close() {
        this.classList.remove('gma-free-shipping--qualified');
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');

        if (this.closeTimer) {
          window.clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }

        const finalize = () => {
          if (this.classList.contains('is-open')) return;
          this.setAttribute('hidden', '');
          if (this.messageEl) {
            this.messageEl.textContent = '';
            this.messageEl.classList.remove('is-fading');
          }
          this.setProgress(0, false);
        };

        if (this.reduceMotion) {
          finalize();
          return;
        }

        const onEnd = (event) => {
          if (event.target !== this || event.propertyName !== 'opacity') return;
          this.removeEventListener('transitionend', onEnd);
          if (this.closeTimer) {
            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
          }
          finalize();
        };

        this.addEventListener('transitionend', onEnd);
        this.closeTimer = window.setTimeout(() => {
          this.removeEventListener('transitionend', onEnd);
          this.closeTimer = null;
          finalize();
        }, 400);
      }
    }
  );
}
